import React, { useState, useEffect } from 'react';
import { 
  Box, FormControl, InputLabel, Select, MenuItem, Button, Typography, 
  Alert, CircularProgress, Chip, Dialog, DialogTitle, DialogContent, 
  DialogActions, FormControlLabel, Switch, Tabs, Tab, IconButton, 
  Collapse, Paper, Divider
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Plot from 'react-plotly.js';
import axios from 'axios';
import { getSensorLabel, maxStrainIndexFromLabels } from '../utils/sensorDisplayName';

const Chart = ({ compact = false, pier = 1, hideTabsAndImage = false, sensorLabels = {} }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [statusData, setStatusData] = useState({ timestamps: [], status: [] });
  const [panelTempData, setPanelTempData] = useState([]);
  const [strainData, setStrainData] = useState({});
  const [temperatureData, setTemperatureData] = useState({});
  const [selectedStrain, setSelectedStrain] = useState('Strain(1)');
  const [predictions, setPredictions] = useState({
    status: null,
    strains: {}
  });
  const [anomalyData, setAnomalyData] = useState(null);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filteredStatusData, setFilteredStatusData] = useState({ timestamps: [], status: [] });
  const [filteredPanelTemp, setFilteredPanelTemp] = useState([]);
  const [filteredStrainData, setFilteredStrainData] = useState({});
  const [filteredTemperatureData, setFilteredTemperatureData] = useState({});
  const [selectedPlotType, setSelectedPlotType] = useState('status');
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [plotDialogOpen, setPlotDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('SS.C');
  const [structureImage, setStructureImage] = useState('/new_bridge_rendering.png'); // Default image

  // Map tab values to image filenames
  const tabImageMap = {
    'Navigation': 'navigation.png',
    'Super Structures': 'super_structures.png',
    'SS.A': 'ss_a.png',
    'SS.B': 'ss_b.png',
    'SS.C': 'ss_c.png',
    'SS.D': 'ss_d.png',
    'SS.E': 'ss_e.png',
    'SS.F': 'ss_f.png',
    'SS.G': 'ss_g.png',
    'SS.H': 'ss_h.png',
    'SS.I': 'ss_i.png'
  };

  // Update image when tab changes
  useEffect(() => {
    const imageFileName = tabImageMap[selectedTab];
    if (imageFileName) {
      // Try to load the image, fallback to default if not found
      const img = new Image();
      img.onerror = () => {
        // Image not found, use default
        setStructureImage('/new_bridge_rendering.png');
      };
      img.onload = () => {
        // Image found, use it
        setStructureImage(`/${imageFileName}`);
      };
      img.src = `/${imageFileName}`;
    } else {
      setStructureImage('/new_bridge_rendering.png');
    }
  }, [selectedTab]);
  
  // Status calibration (zeros from first filtered point when date filter is applied)
  const [isStatusCalibrated, setIsStatusCalibrated] = useState(false);
  const [calibratedStatusData, setCalibratedStatusData] = useState({ timestamps: [], status: [] });

  // Strain zeroing (calibration) flag
  const [isStrainCalibrated, setIsStrainCalibrated] = useState(false);

  const strainOptionCount = Math.max(17, maxStrainIndexFromLabels(sensorLabels));
  const strainOptions = Array.from({ length: strainOptionCount }, (_, i) => `Strain(${i + 1})`);
  const temperatureOptions = Array.from({ length: strainOptionCount }, (_, i) => `TT(${i + 1})`);
  const availableSensorOptions = selectedPlotType === 'temperature' ? temperatureOptions : strainOptions;
  const sensorLabel = (sensorId) => getSensorLabel(sensorId, sensorLabels);

  const seriesHasValues = (series) =>
    Array.isArray(series) && series.some((v) => v !== null && v !== undefined && !Number.isNaN(v));

  // Load existing data on component mount / pier change
  useEffect(() => {
    let cancelled = false;
    const loadExistingData = async () => {
      try {
        setLoading(true);
        setError(null);
        setStatusData({ timestamps: [], status: [] });
        setFilteredStatusData({ timestamps: [], status: [] });
        setPanelTempData([]);
        setFilteredPanelTemp([]);
        setStrainData({});
        setFilteredStrainData({});
        setTemperatureData({});
        setFilteredTemperatureData({});
        setSelectedSensors([]);

        // Pier 4 B27 is ~12MB full; downsample for Monitor so plots actually render.
        const plotResponse = await axios.get(`/api/plot-data?pier=${pier}`, {
          timeout: 180000,
          params: { max_points: 12000 },
        });
        if (cancelled) return;
        if (!plotResponse.data.error) {
          // Load status data if available
          if (plotResponse.data.timestamps && plotResponse.data.status) {
            const newStatusData = {
              timestamps: plotResponse.data.timestamps,
              status: plotResponse.data.status
            };
            setStatusData(newStatusData);
            setFilteredStatusData(newStatusData); // Also set filtered data immediately
          }

          if (Array.isArray(plotResponse.data.panel_temp)) {
            setPanelTempData(plotResponse.data.panel_temp);
            setFilteredPanelTemp(plotResponse.data.panel_temp);
          }
          
          // Load strain data if available
          if (plotResponse.data.strains) {
            const newStrainData = plotResponse.data.strains;
            setStrainData(newStrainData);
            setFilteredStrainData(newStrainData);
            
            // Auto-select strain gauges with actual data
            const availableStrains = [];
            for (const strainType of strainOptions) {
              if (seriesHasValues(newStrainData[strainType])) {
                availableStrains.push(strainType);
                if (!selectedStrain) {
                  setSelectedStrain(strainType);
                }
              }
            }
            
            // Auto-select first few strain gauges for plotting
            if (availableStrains.length > 0) {
              const strainsToSelect = availableStrains.slice(0, 3); // Select first 3 strain gauges
              setSelectedSensors(strainsToSelect);
            }
          }

          if (plotResponse.data.temperatures) {
            const newTemperatureData = plotResponse.data.temperatures;
            setTemperatureData(newTemperatureData);
            setFilteredTemperatureData(newTemperatureData);
          }
        } else {
          setError(plotResponse.data.error || `No plot data for pier ${pier}`);
        }
      } catch (err) {
        if (!cancelled) {
          setError(`Could not load pier ${pier} plot data: ${err.message}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    
    loadExistingData();
    return () => { cancelled = true; };
  }, [pier]);

  // When switching to temperature (or back to strain), keep sensor picks in that family
  useEffect(() => {
    if (selectedPlotType === 'temperature') {
      const availableTemps = temperatureOptions.filter((id) =>
        seriesHasValues(temperatureData[id] || filteredTemperatureData[id])
      );
      const keep = selectedSensors.filter((s) => s.startsWith('TT('));
      if (keep.length === 0 && availableTemps.length > 0) {
        setSelectedSensors(availableTemps.slice(0, 3));
      } else if (keep.length !== selectedSensors.length) {
        setSelectedSensors(keep);
      }
    } else if (selectedPlotType === 'strain') {
      const availableStrains = strainOptions.filter((id) =>
        seriesHasValues(strainData[id] || filteredStrainData[id])
      );
      const keep = selectedSensors.filter((s) => s.startsWith('Strain('));
      if (keep.length === 0 && availableStrains.length > 0) {
        setSelectedSensors(availableStrains.slice(0, 3));
      } else if (keep.length !== selectedSensors.length) {
        setSelectedSensors(keep);
      }
    }
  }, [selectedPlotType, temperatureData, strainData]);

  // Auto-filter data when dates or source data changes
  useEffect(() => {
    filterDataByDate();
  }, [startDate, endDate, statusData, strainData, temperatureData, panelTempData]);

  const convertTimestampsToDate = (timestamps) => {
    if (!timestamps || timestamps.length === 0) {
      return [];
    }
    
    
    const dates = timestamps.map(ts => {
      let timestamp = ts;
      
      // Check if timestamp is in seconds (Unix epoch) or milliseconds
      if (ts < 4102444800) {
        timestamp = ts * 1000;
      } else if (ts > 4102444800000) {
        const testDate = new Date(ts);
        if (testDate.getFullYear() < 1970 || testDate.getFullYear() > 2050) {
          timestamp = ts / 1000;
        } else {
          timestamp = ts;
        }
      } else {
        timestamp = ts;
      }
      
      const date = new Date(timestamp);
      return date;
    });
    
    return dates;
  };

  // Filter data by date range
  const filterDataByDate = () => {
    
    // Reset calibration when filter changes
    setIsStatusCalibrated(false);
    setCalibratedStatusData({ timestamps: [], status: [] });
    
    if (!startDate || !endDate) {
      setFilteredStatusData(statusData);
      setFilteredPanelTemp(panelTempData);
      setFilteredStrainData(strainData);
      setFilteredTemperatureData(temperatureData);
      return;
    }

    // Convert to full-day local range
    const startOfDay = new Date(startDate);
    startOfDay.setHours(0, 0, 0, 0);
    const startTimestamp = startOfDay.getTime() / 1000;
    
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);
    const endTimestamp = endOfDay.getTime() / 1000;
    

    // Filter status data
    const filteredStatus = {
      timestamps: [],
      status: []
    };
    const filteredPanel = [];

    statusData.timestamps.forEach((timestamp, index) => {
      const normalizedTimestamp = timestamp > 4102444800 ? timestamp / 1000 : timestamp;
      if (normalizedTimestamp >= startTimestamp && normalizedTimestamp <= endTimestamp) {
        filteredStatus.timestamps.push(timestamp);
        filteredStatus.status.push(statusData.status[index]);
        if (panelTempData && panelTempData[index] !== undefined) {
          filteredPanel.push(panelTempData[index]);
        }
      }
    });

    setFilteredStatusData(filteredStatus);
    setFilteredPanelTemp(filteredPanel);

    // Show success message with filter results
    const startDateStr = startOfDay.toLocaleDateString();
    const endDateStr = endOfDay.toLocaleDateString();
    setSuccess(`Date filter applied: ${filteredStatus.timestamps.length} data points from ${startDateStr} to ${endDateStr}`);
    setTimeout(() => setSuccess(null), 5000);

    // Filter strain data using status timestamps
    const filteredStrain = {};
    Object.keys(strainData).forEach(strainType => {
      filteredStrain[strainType] = [];
    });

    statusData.timestamps.forEach((timestamp, index) => {
      const normalizedTimestamp = timestamp > 4102444800 ? timestamp / 1000 : timestamp;
      if (normalizedTimestamp >= startTimestamp && normalizedTimestamp <= endTimestamp) {
        Object.keys(strainData).forEach(strainType => {
          if (strainData[strainType] && strainData[strainType][index] !== undefined) {
            filteredStrain[strainType].push(strainData[strainType][index]);
          }
        });
      }
    });

    setFilteredStrainData(filteredStrain);

    // Filter temperature data using status timestamps
    const filteredTemp = {};
    Object.keys(temperatureData).forEach((tempType) => {
      filteredTemp[tempType] = [];
    });

    statusData.timestamps.forEach((timestamp, index) => {
      const normalizedTimestamp = timestamp > 4102444800 ? timestamp / 1000 : timestamp;
      if (normalizedTimestamp >= startTimestamp && normalizedTimestamp <= endTimestamp) {
        Object.keys(temperatureData).forEach((tempType) => {
          if (temperatureData[tempType] && temperatureData[tempType][index] !== undefined) {
            filteredTemp[tempType].push(temperatureData[tempType][index]);
          }
        });
      }
    });

    setFilteredTemperatureData(filteredTemp);
  };

  const clearDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setFilteredStatusData(statusData);
    setFilteredPanelTemp(panelTempData);
    setFilteredStrainData(strainData);
    setFilteredTemperatureData(temperatureData);
    
    // Reset calibration when clearing filter
    setIsStatusCalibrated(false);
    setCalibratedStatusData({ timestamps: [], status: [] });
    
    setSuccess(`Date filter cleared. Showing all ${statusData.timestamps.length} data points.`);
    setTimeout(() => setSuccess(null), 3000);
  };

  // Apply status calibration - zeros from FIRST FILTERED DATA POINT (start date)
  const handleApplyStatusCalibration = () => {
    if (!filteredStatusData.status || !filteredStatusData.status.length) {
      setError('No status data available to calibrate');
      return;
    }
    
    // Use the FIRST value in filtered data as calibration baseline
    const calibrationValue = filteredStatusData.status[0];
    
    if (calibrationValue === undefined || calibrationValue === null) {
      setError('Invalid calibration point - first data point is null');
      return;
    }
    
    const newCalibratedData = {
      timestamps: [...filteredStatusData.timestamps],
      status: filteredStatusData.status.map(value => 
        (value !== undefined && value !== null) ? value - calibrationValue : value
      )
    };
    
    setCalibratedStatusData(newCalibratedData);
    setIsStatusCalibrated(true);
    
    const calibDate = new Date(filteredStatusData.timestamps[0] > 4102444800 ? 
      filteredStatusData.timestamps[0] : filteredStatusData.timestamps[0] * 1000);
    setSuccess(`Status calibration applied! Zeroed from ${calibDate.toLocaleString()} (Baseline: ${calibrationValue.toFixed(3)}V)`);
    setTimeout(() => setSuccess(null), 5000);
  };

  // Reset status calibration
  const handleResetStatusCalibration = () => {
    setIsStatusCalibrated(false);
    setCalibratedStatusData({ timestamps: [], status: [] });
    setSuccess('Status calibration reset to original data');
    setTimeout(() => setSuccess(null), 3000);
  };

  const fetchPredictions = async (type, data) => {
    try {
      if (!data || (Array.isArray(data) && data.length === 0)) return;

      const response = await axios.get('/api/predict');
      if (type === 'status') {
        setPredictions(prev => ({
          ...prev,
          status: response.data.status
        }));
      } else if (type === 'strain') {
        setPredictions(prev => ({
          ...prev,
          strains: response.data.strains
        }));
      }
    } catch (err) {
      console.error('Error fetching predictions:', err);
    }
  };

  // Sensor selection handlers
  const handleSensorSelect = (sensor) => {
    if (!selectedSensors.includes(sensor)) {
      setSelectedSensors([...selectedSensors, sensor]);
    }
  };

  const handleSensorDeselect = (sensor) => {
    setSelectedSensors(selectedSensors.filter(s => s !== sensor));
  };

  const clearSelectedSensors = () => {
    setSelectedSensors([]);
  };

  // Check if plotting conditions are met
  const canShowPlot = () => {
    if ((selectedPlotType === 'strain' || selectedPlotType === 'temperature') && selectedSensors.length === 0) {
      return false;
    }
    return true;
  };

  // Helper – do any selected sensors have any strain data?
  const hasStrainDataForSelectedSensors = selectedSensors.some(sensor => {
    const arr = filteredStrainData[sensor] || strainData[sensor];
    return seriesHasValues(arr);
  });

  const hasTemperatureDataForSelectedSensors = selectedSensors.some((sensor) => {
    const arr = filteredTemperatureData[sensor] || temperatureData[sensor];
    return seriesHasValues(arr);
  });

  // Get message for why plotting is not available
  const getPlotMessage = () => {
    if (selectedPlotType === 'strain' && selectedSensors.length === 0) {
      return 'Please select strain sensors to plot.';
    }

    if (selectedPlotType === 'temperature' && selectedSensors.length === 0) {
      return 'Please select temperature sensors to plot.';
    }
    
    if (selectedPlotType === 'status' && filteredStatusData.timestamps.length === 0) {
      return 'No status data available. Please upload data to begin.';
    }

    if (selectedPlotType === 'panel_temp' && !seriesHasValues(filteredPanelTemp)) {
      return 'No panel temperature data available for this pier.';
    }
    
    if (selectedPlotType === 'strain' && !hasStrainDataForSelectedSensors) {
      return 'No strain data available for selected sensors.';
    }

    if (selectedPlotType === 'temperature' && !hasTemperatureDataForSelectedSensors) {
      return 'No temperature data available for selected sensors.';
    }
    
    return 'No data available.';
  };

  // Export data as CSV
  const handleExportData = async () => {
    try {
      setLoading(true);
      
      if ((selectedPlotType === 'strain' || selectedPlotType === 'temperature') && selectedSensors.length === 0) {
        setError(`Please select ${selectedPlotType} sensors to export.`);
        return;
      }

      const exportParams = {
        plotType: selectedPlotType,
        pier,
        selectedSensors:
          selectedPlotType === 'strain' || selectedPlotType === 'temperature'
            ? selectedSensors.join(',')
            : null
      };

      if (startDate && endDate) {
        const startOfDay = new Date(startDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        
        const formatLocalDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        };
        
        exportParams.startDate = formatLocalDate(startOfDay);
        exportParams.endDate = formatLocalDate(endOfDay);
      }

      const response = await axios.get('/api/export-filtered-data', {
        params: exportParams
      });

      if (response.data.error) {
        setError(response.data.error);
        return;
      }

      const csvContent = response.data.csv;
      const filename = response.data.filename || `filtered_data_${selectedPlotType}_${new Date().toISOString().split('T')[0]}.csv`;
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('Filtered data exported successfully!');
    } catch (err) {
      setError('Failed to export data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStrainChange = async (event) => {
    const newStrainType = event.target.value;
    setSelectedStrain(newStrainType);
    
    if (strainData[newStrainType]?.length > 0) {
      try {
        const anomalyResponse = await axios.get(`/api/detect-anomalies?strain_type=${newStrainType}`);
        setAnomalyData(anomalyResponse.data);
      } catch (err) {
        console.error('Error fetching anomalies:', err);
      }
    }
  };

  // Adjust chart size and button size for compact mode
  const chartWidth = compact ? undefined : 1300;
  const chartHeight = compact ? 360 : 600;
  const buttonSize = compact ? 'small' : 'medium';

  // Use calibrated data if calibration is active, otherwise use filtered data (status)
  const displayStatusData = isStatusCalibrated ? calibratedStatusData : filteredStatusData;

  const statusPlotConfig = {
    data: [{
      type: 'scattergl',
      mode: 'lines+markers',
      x: convertTimestampsToDate(displayStatusData.timestamps || []),
      y: displayStatusData.status || [],
      line: { color: '#1976d2', width: 2 },
      marker: { size: 4, color: '#1976d2' },
      name: 'Status'
    }],
    layout: {
      title: isStatusCalibrated ? 'Status vs Time (Calibrated)' : 'Status vs Time',
      xaxis: { 
        title: 'Timestamp (Date)',
        type: 'date',
        tickangle: -45,
        autorange: true
      },
      yaxis: { 
        title: isStatusCalibrated ? 'Status (Calibrated)' : 'Status',
        autorange: true
      },
      autosize: true,
      ...(chartWidth ? { width: chartWidth } : {}),
      height: chartHeight,
      margin: { l: 80, r: 40, t: 60, b: 100 },
      showlegend: true,
      legend: { x: 0, y: 1.1, orientation: 'h' }
    },
    config: {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d']
    }
  };

  const panelTempPlotConfig = {
    data: [{
      type: 'scattergl',
      mode: 'lines+markers',
      x: convertTimestampsToDate(filteredStatusData.timestamps || []),
      y: filteredPanelTemp || [],
      line: { color: '#d84315', width: 2 },
      marker: { size: 4, color: '#d84315' },
      name: 'Panel Temperature'
    }],
    layout: {
      title: 'Panel Temperature vs Time',
      xaxis: {
        title: 'Timestamp (Date)',
        type: 'date',
        tickangle: -45,
        autorange: true
      },
      yaxis: {
        title: 'Panel Temperature (°C)',
        autorange: true
      },
      autosize: true,
      ...(chartWidth ? { width: chartWidth } : {}),
      height: chartHeight,
      margin: { l: 80, r: 40, t: 60, b: 100 },
      showlegend: true,
      legend: { x: 0, y: 1.1, orientation: 'h' }
    },
    config: {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d']
    }
  };

  const plotColors = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];

  const strainPlotConfig = {
    data: selectedSensors.map((sensor, index) => {
      const rawStrain = filteredStrainData[sensor] || [];

      // Zeroing: subtract first value if enabled
      let yValues = rawStrain;
      if (isStrainCalibrated && rawStrain.length > 0) {
        const baseline = rawStrain.find(v => v !== null && v !== undefined);
        if (baseline !== undefined) {
          yValues = rawStrain.map(v =>
            v !== null && v !== undefined ? v - baseline : null
          );
        }
      }

      return {
        type: 'scattergl',
        mode: 'lines',
        x: convertTimestampsToDate(filteredStatusData.timestamps || []),
        y: yValues,
        line: { color: plotColors[index % plotColors.length], width: 2 },
        name: sensorLabel(sensor)
      };
    }),
    layout: {
      title: selectedSensors.length === 1
        ? `${sensorLabel(selectedSensors[0])} vs Time${isStrainCalibrated ? ' (Zeroed)' : ''}`
        : `${selectedSensors.length} Strain Gauges vs Time${isStrainCalibrated ? ' (Zeroed)' : ''}`,
      xaxis: { 
        title: 'Timestamp (Date)',
        type: 'date',
        tickangle: -45,
        autorange: true
      },
      yaxis: { 
        title: isStrainCalibrated ? 'Strain (Microstrain, Zeroed)' : 'Strain (Microstrain)',
        autorange: true
      },
      autosize: true,
      ...(chartWidth ? { width: chartWidth } : {}),
      height: chartHeight,
      margin: { l: 80, r: 40, t: 60, b: 100 },
      showlegend: true,
      legend: { x: 0, y: 1.1, orientation: 'h' }
    },
    config: {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d']
    }
  };

  const temperaturePlotConfig = {
    data: selectedSensors.map((sensor, index) => ({
      type: 'scattergl',
      mode: 'lines',
      x: convertTimestampsToDate(filteredStatusData.timestamps || []),
      y: filteredTemperatureData[sensor] || [],
      line: { color: plotColors[index % plotColors.length], width: 2 },
      name: sensorLabel(sensor)
    })),
    layout: {
      title: selectedSensors.length === 1
        ? `${sensorLabel(selectedSensors[0])} vs Time`
        : `${selectedSensors.length} Temperature Sensors vs Time`,
      xaxis: {
        title: 'Timestamp (Date)',
        type: 'date',
        tickangle: -45,
        autorange: true
      },
      yaxis: {
        title: 'Temperature (°C)',
        autorange: true
      },
      autosize: true,
      ...(chartWidth ? { width: chartWidth } : {}),
      height: chartHeight,
      margin: { l: 80, r: 40, t: 60, b: 100 },
      showlegend: true,
      legend: { x: 0, y: 1.1, orientation: 'h' }
    },
    config: {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d']
    }
  };

  const renderPredictionInfo = (predictionData) => {
    if (!predictionData || predictionData.error) {
      return (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {predictionData?.error || 'No prediction data available'}
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
        {/* Metrics Circles */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          alignItems: 'center',
          mb: 4,
          mt: 2,
          gap: 4
        }}>
          {/* R² Score Circle */}
          <Box sx={{ 
            position: 'relative', 
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1
          }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, predictionData.r2_score * 100))}
                size={120}
                thickness={3}
                sx={{
                  color: '#4CAF50',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 'bold',
                    color: '#2E7D32'
                  }}
                >
                  {(predictionData.r2_score * 100).toFixed(0)}%
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: 'text.secondary',
                    mt: 0.5
                  }}
                >
                  R² Score
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* RMSE Circle */}
          <Box sx={{ 
            position: 'relative', 
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            flex: 1
          }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, 100 - (predictionData.rmse * 10)))}
                size={120}
                thickness={3}
                sx={{
                  color: '#2196F3',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="h4"
                  component="div"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1565C0'
                  }}
                >
                  {predictionData.rmse.toFixed(2)}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: 'text.secondary',
                    mt: 0.5
                  }}
                >
                  RMSE
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Future Predictions */}
        <Box sx={{ 
          mt: 3, 
          pt: 3, 
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
            Predicted Values
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            mt: 2,
            justifyContent: 'space-around',
            overflowX: 'auto',
            pb: 2
          }}>
            {predictionData.future_predictions.map((pred, idx) => (
              <Box key={idx} sx={{ 
                p: 2, 
                bgcolor: 'action.hover', 
                borderRadius: 2,
                minWidth: 100,
                flex: '0 0 auto',
                textAlign: 'center',
                boxShadow: 1
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Step {idx + 1}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  {pred.toFixed(3)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderAnomalyInfo = () => {
    if (!anomalyData || anomalyData.anomaly_dates.length === 0) {
      return (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          No anomalies detected
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
        <Typography variant="h6" gutterBottom>
          Anomaly Detection Results
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {anomalyData.total_anomaly_days || anomalyData.anomaly_dates.length} days with unusual strain patterns detected:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {anomalyData.anomaly_dates.map((date, index) => (
            <Chip
              key={index}
              label={date}
              color="error"
              variant="outlined"
              size="small"
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* Blue Tabs Bar - At the top like in the reference image */}
      {!hideTabsAndImage && (
        <Box sx={{ 
          maxWidth: compact ? 1400 : 1400, 
          mx: 'auto',
          mt: 2,
          mb: 2,
          borderBottom: '2px solid #e0e0e0',
          bgcolor: '#f5f5f5',
          borderRadius: '4px 4px 0 0',
          px: 1
        }}>
          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#666',
                padding: '12px 16px',
                '&.Mui-selected': {
                  color: '#1976d2',
                  fontWeight: 600,
                  backgroundColor: '#e3f2fd'
                },
                '&:hover': {
                  backgroundColor: '#f0f0f0'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
                height: 3
              }
            }}
          >
            <Tab label="Navigation" value="Navigation" />
            <Tab label="Super Structures" value="Super Structures" />
            <Tab label="SS.A" value="SS.A" />
            <Tab label="SS.C" value="SS.C" />
            <Tab label="SS.E" value="SS.E" />
            <Tab label="SS.G" value="SS.G" />
            <Tab label="SS.H" value="SS.H" />
            <Tab label="SS.I" value="SS.I" />
          </Tabs>
        </Box>
      )}

      {/* Bridge Rendering Image - Changes based on selected tab */}
      {!hideTabsAndImage && (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mt: '62px',
        mb: 3,
        px: 2
      }}>
        <img 
            src={structureImage}
            alt={`Structure Image - ${selectedTab}`}
          style={{
            maxWidth: '100%',
            width: '90%',
            height: 'auto',
            maxHeight: '350px',
            borderRadius: '12px',
              boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
              objectFit: 'contain'
            }}
            onError={(e) => {
              // Fallback to default image if the specified image doesn't exist
              e.target.src = '/new_bridge_rendering.png';
          }}
        />
      </Box>
      )}

      {/* Main Data Visualization Container */}
      <Box sx={{ 
        p: compact ? 1 : 3, 
        maxWidth: compact ? 1400 : 1400, 
        mx: 'auto',
        mt: 0,
        minHeight: 'auto',
        paddingBottom: '40px'
      }}>

        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mt: 2, mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 2,
            justifyContent: 'center',
            p: 3
          }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography>
              Processing...
            </Typography>
          </Box>
        )}

        <Box sx={{ 
          p: 3, 
          border: '1px solid #e0e0e0', 
          borderRadius: 1,
          mb: 3,
          bgcolor: 'background.paper'
        }}>
          <Typography variant="h6" gutterBottom>
            Data Visualization Controls
          </Typography>
            
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Plot Type
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <Select
                  value={selectedPlotType}
                  onChange={(e) => setSelectedPlotType(e.target.value)}
                  disabled={loading}
                  size={buttonSize}
                >
                  <MenuItem value="status">Status Plot</MenuItem>
                  <MenuItem value="panel_temp">Panel Temperature Plot</MenuItem>
                  <MenuItem value="strain">Strain Plot</MenuItem>
                  <MenuItem value="temperature">Temperature Plot</MenuItem>
                </Select>
              </FormControl>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => setDateDialogOpen(true)}
                  disabled={loading}
                  size={buttonSize}
                  sx={{ 
                    backgroundColor: '#1976d2',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1565c0'
                    },
                    minWidth: 'fit-content'
                  }}
                >
                  Filter by Dates
                </Button>
                
                {(startDate || endDate) && (
                  <Button 
                    variant="outlined" 
                    onClick={clearDateFilter}
                    disabled={loading}
                    color="secondary"
                    size={buttonSize}
                    sx={{ minWidth: 'fit-content' }}
                  >
                    Clear Filter
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
            
          {/* Sensors Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Sensors
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {/* Available Sensors List */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Available Sensors:
                </Typography>
                <Box sx={{ 
                  border: '1px solid #ccc', 
                  borderRadius: 1, 
                  height: 120, 
                  overflowY: 'auto',
                  bgcolor: 'background.paper',
                  p: 1
                }}>
                  {availableSensorOptions.map((sensor) => (
                    <Box 
                      key={sensor}
                      sx={{ 
                        p: 0.5, 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 0.5
                      }}
                      onClick={() => handleSensorSelect(sensor)}
                    >
                      {sensorLabel(sensor)}
                    </Box>
                  ))}
                </Box>
              </Box>
              
              {/* Selected Sensors */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Selected:
                </Typography>
                <Box sx={{ 
                  border: '1px solid #ccc', 
                  borderRadius: 1, 
                  height: 120, 
                  overflowY: 'auto',
                  bgcolor: 'background.paper',
                  p: 1
                }}>
                  {selectedSensors.map((sensor) => (
                    <Box 
                      key={sensor}
                      sx={{ 
                        p: 0.5, 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 0.5,
                        bgcolor: 'primary.light',
                        color: 'white'
                      }}
                      onClick={() => handleSensorDeselect(sensor)}
                    >
                      {sensorLabel(sensor)}
                    </Box>
                  ))}
                </Box>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={clearSelectedSensors}
                  sx={{ mt: 1 }}
                >
                  Clear List
                </Button>
              </Box>
            </Box>
          </Box>
            
          {selectedPlotType === 'strain' && (
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isStrainCalibrated}
                    onChange={(e) => setIsStrainCalibrated(e.target.checked)}
                  />
                }
                label="Zero strain (subtract first value)"
                sx={{ ml: 0 }}
              />
            </Box>
          )}
            
          {/* Plot and Export Buttons */}
          <Box sx={{ mb: 3, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              onClick={() => setPlotDialogOpen(true)}
              disabled={loading || !canShowPlot()}
              size="large"
              sx={{ 
                minWidth: 200,
                backgroundColor: '#1976d2',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#1565c0'
                }
              }}
            >
              Plot
            </Button>
            <Button 
              variant="outlined" 
              onClick={handleExportData}
              disabled={loading}
              size="large"
              sx={{ 
                minWidth: 200,
                borderColor: '#4caf50',
                color: '#4caf50',
                '&:hover': {
                  borderColor: '#388e3c',
                  color: '#388e3c',
                  backgroundColor: 'rgba(76, 175, 80, 0.04)'
                }
              }}
            >
              Export Data
            </Button>
          </Box>
        </Box>

        {/* Plot Dialog */}
        <Dialog
          open={plotDialogOpen} 
          onClose={() => setPlotDialogOpen(false)}
          maxWidth="xl"
          fullWidth
        >
          <DialogTitle>
            {selectedPlotType === 'status'
              ? 'Status Data Plot'
              : selectedPlotType === 'panel_temp'
                ? 'Panel Temperature Plot'
                : selectedPlotType === 'temperature'
                  ? 'Temperature Data Plot'
                  : 'Strain Data Plot'}
          </DialogTitle>
          <DialogContent>
            {/* Status Calibration Panel - Simple Apply/Reset buttons (NO DROPDOWN) */}
            {selectedPlotType === 'status' && filteredStatusData.timestamps.length > 0 && startDate && endDate && (
              <Box sx={{ 
                p: 2, 
                mb: 3, 
                border: '2px solid #1976d2', 
                borderRadius: 2,
                bgcolor: 'background.paper'
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                  Status Calibration / Zeroing {isStatusCalibrated && <Chip label="Active" color="primary" size="small" sx={{ ml: 1 }} />}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Calibration will zero the status data from the first data point at your selected start date: <strong>{startDate.toLocaleDateString()}</strong>
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {!isStatusCalibrated ? (
                    <Button 
                      variant="contained" 
                      onClick={handleApplyStatusCalibration}
                      sx={{ minWidth: 150 }}
                    >
                      Apply Calibration
                    </Button>
                  ) : (
                    <Button 
                      variant="outlined" 
                      color="secondary"
                      onClick={handleResetStatusCalibration}
                      sx={{ minWidth: 150 }}
                    >
                      Reset Calibration
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            <Box sx={{ pt: 2 }}>
              {selectedPlotType === 'status' ? (
                // Status Plot Display
                canShowPlot() && filteredStatusData.timestamps.length > 0 ? (
                  <Box sx={{ position: 'relative' }}>
                    <Plot
                      data={statusPlotConfig.data}
                      layout={statusPlotConfig.layout}
                      config={statusPlotConfig.config}
                      style={{ width: '100%', height: '100%' }}
                    />
                    {predictions.status && renderPredictionInfo(predictions.status)}
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 3, 
                    textAlign: 'center', 
                    color: 'text.secondary',
                    bgcolor: 'action.hover',
                    borderRadius: 1
                  }}>
                    <Typography>
                      {getPlotMessage()}
                    </Typography>
                  </Box>
                )
              ) : selectedPlotType === 'panel_temp' ? (
                canShowPlot() && seriesHasValues(filteredPanelTemp) ? (
                  <Box sx={{ position: 'relative' }}>
                    <Plot
                      data={panelTempPlotConfig.data}
                      layout={panelTempPlotConfig.layout}
                      config={panelTempPlotConfig.config}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                ) : (
                  <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    color: 'text.secondary',
                    bgcolor: 'action.hover',
                    borderRadius: 1
                  }}>
                    <Typography>
                      {getPlotMessage()}
                    </Typography>
                  </Box>
                )
              ) : selectedPlotType === 'temperature' ? (
                canShowPlot() && hasTemperatureDataForSelectedSensors ? (
                  <Box sx={{ position: 'relative' }}>
                    <Plot
                      data={temperaturePlotConfig.data}
                      layout={temperaturePlotConfig.layout}
                      config={temperaturePlotConfig.config}
                      style={{ width: '100%', height: '100%' }}
                    />
                  </Box>
                ) : (
                  <Box sx={{
                    p: 3,
                    textAlign: 'center',
                    color: 'text.secondary',
                    bgcolor: 'action.hover',
                    borderRadius: 1
                  }}>
                    <Typography>
                      {getPlotMessage()}
                    </Typography>
                  </Box>
                )
              ) : (
                // Strain Plot Display
                canShowPlot() && hasStrainDataForSelectedSensors ? (
                  <Box sx={{ position: 'relative' }}>
                    <Plot
                      data={strainPlotConfig.data}
                      layout={strainPlotConfig.layout}
                      config={strainPlotConfig.config}
                      style={{ width: '100%', height: '100%' }}
                    />
                    {predictions.strains[selectedStrain] && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Prediction Metrics
                        </Typography>
                        {renderPredictionInfo(predictions.strains[selectedStrain])}
                      </Box>
                    )}
                    {anomalyData && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Anomaly Detection
                        </Typography>
                        {renderAnomalyInfo()}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ 
                    p: 3, 
                    textAlign: 'center', 
                    color: 'text.secondary',
                    bgcolor: 'action.hover',
                    borderRadius: 1
                  }}>
                    <Typography>
                      {getPlotMessage()}
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPlotDialogOpen(false)} size={buttonSize}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Date Filter Dialog */}
        <Dialog open={dateDialogOpen} onClose={() => setDateDialogOpen(false)}>
          <DialogTitle>Filter Data by Date Range</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Data will be filtered from 12:00 AM of the start date to 11:59 PM of the end date.
              </Typography>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
                size={buttonSize}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
                size={buttonSize}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDateDialogOpen(false)} size={buttonSize}>Cancel</Button>
            <Button 
              onClick={() => {
                filterDataByDate();
                setDateDialogOpen(false);
              }} 
              variant="contained" 
              size={buttonSize}
            >
              Apply Filter
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Chart;